import { useEffect, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { Button, ViewsNav, type Navigate } from "@omnabase/ui";
import type { Tag } from "@omnabase/shared";
import type { TicketCounts } from "../../lib/inbox";
import { useSidebar } from "../../lib/sidebar";
import { useSetParam } from "../../lib/nav";
import { toggleFolded } from "../../lib/sidebarEdits";
import { inboxViewGroups } from "./inboxViews";
import { CustomizeSidebar, inboxArea } from "./CustomizeSidebar";

/** The inbox's views column as the person arranged it (the sidebar and consent round, 2026-10-24): the inbox and the "Unknown address" page draw
 *  the same one. A heading folds its group (saved with the layout); "Customize", the column's sticky foot (2026-10-26), opens the dialog
 *  (`?customize=1`), and the column follows it live. A column with no group shown says so in one quiet line (the sidebar editor rework).
 *  After a save the counts are read again: the column may show other tags now. */
export function InboxColumn({ view, tagId, projectId, counts, tags, viewHref, tagHref, scope, onNavigate }: {
  view: string; tagId: string | null; projectId: string | null; counts: { data: TicketCounts | null; reload: () => Promise<void> }; tags: readonly Tag[];
  viewHref: (id: string) => string; tagHref: (id: string) => string; scope?: ReactNode; onNavigate: Navigate;
}) {
  const sidebar = useSidebar();
  const [params] = useSearchParams(); const setParam = useSetParam();
  const customizing = params.get("customize") === "1"; // the dialog's place in the address, as the app's other dialogs have it
  useEffect(() => { if (sidebar.saved) void counts.reload(); }, [sidebar.saved]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => inboxViewGroups({ view, tagId, counts: counts.data, layout: sidebar.layout, tags, projectId, viewHref, tagHref, onFold: (id) => sidebar.change(toggleFolded(sidebar.layout, id)) }),
    [view, tagId, counts.data, sidebar.layout, tags, projectId, viewHref, tagHref]); // eslint-disable-line react-hooks/exhaustive-deps
  const footer = <Button variant="ghost" size="sm" icon="tune" onClick={() => setParam("customize", "1")}>Customize</Button>;
  return (
    <>
      <ViewsNav groups={groups} scope={scope} footer={footer} empty="Nothing shown. Customize the sidebar." tour="views" onNavigate={onNavigate} />
      {customizing && <CustomizeSidebar store={sidebar} area={inboxArea(tags)} onClose={() => setParam("customize", null)} />}
    </>
  );
}
